import { MessagingTransferState } from "@/common/constants/messaging";
import { ChannelType, GenericChannelType } from "@/common/constants/shared";
import { StorageKeys } from "@/common/constants/storageKeys";
import { addBreadcrumb, captureError } from "@/common/helpers/sentryHelper";
import { Lockr } from "@/common/Lockr";
import {
	MessagingAcceptTransferRequest,
	MessagingRejectTransferRequest,
} from "@/common/models/messagingTransfer";
import {
	MessagingInviteState,
	useMessagingInviteSessionStore,
} from "@/common/stores/nonpersistingStores/messagingInvite";
import { InteractionActionCard } from "@/components/molecules/interactionActionCard";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { InteractionDirection } from "@/repository/models";
import { WebchatDefaultCustomerName } from "@/common/constants";

interface InteractionCardBaseProps {
	channelType: ChannelType;
	channelConfig: {
		icon: JSX.Element;
		detailsCard: React.FC<any>;
		acceptFn: (messagingId: string) => Promise<any>;
		acceptTransferFn: (request: MessagingAcceptTransferRequest) => void;
		rejectFn: (messagingId: string) => Promise<any>;
		rejectTransferFn: (request: MessagingRejectTransferRequest) => void;
		messageIntlId: string;
	};
}

export const InteractionCardBase = (props: InteractionCardBaseProps) => {
	const { channelType, channelConfig } = props;
	const intl = useIntl();
	const [displayCards, setDisplayCards] = useState<boolean>(false);

	const messagingInviteSession = useMessagingInviteSessionStore(
		(state: MessagingInviteState) => state.messagingSession
	);
	const {
		icon,
		detailsCard: DetailsCard,
		acceptFn,
		acceptTransferFn,
		rejectFn,
		rejectTransferFn,
		messageIntlId,
	} = channelConfig;

	const [intlMessageId, setIntlMessageId] = useState<string>(messageIntlId);

	useEffect(() => {
		if (!messagingInviteSession) {
			setDisplayCards(false);
			return;
		}
		if (
			messagingInviteSession.channelType === channelType &&
			!messagingInviteSession.state
		) {
			const constmsgId = overrideMessage(messagingInviteSession.channelType, messagingInviteSession.genericChannelType);
			setIntlMessageId(constmsgId);
			setDisplayCards(true);
		}
	}, [messagingInviteSession, channelType]);


	const overrideMessage = (channel: ChannelType, genericChannelType?: GenericChannelType): string => {
		if (channel == ChannelType.Generic) {
			if (genericChannelType === undefined) {
				return messageIntlId;
			}

			switch (genericChannelType) {
				case GenericChannelType.Generic:
					return messageIntlId;
				case GenericChannelType.Email:
					return "Interaction.Contents.EmailMessage";
				case GenericChannelType.Chat:
					return "Interaction.Contents.ChatMessage";
				case GenericChannelType.SocialMedia:
					return "Interaction.Contents.SocialMediaMessage";
				case GenericChannelType.WhatsApp:
					return "Interaction.Contents.WhatsAppMessage";
				default:
					return messageIntlId;
			}
		}

		return messageIntlId;
	}

	const rejectMutation = useMutation({
		mutationFn: (messagingId: string) => rejectFn(messagingId),
		onError: (err, data) => {
			captureError(err, "Error rejecting messaging interaction", undefined, {
				messagingId: data,
				type: channelType,
			});
			console.error(err);
		},
	});

	const rejectInteraction = useCallback(() => {
		setDisplayCards(false);

		if (
			messagingInviteSession?.transferState === MessagingTransferState.Offering
		) {
			const userId = Lockr.get(StorageKeys.UserId);

			const rejectTransferRequest: MessagingRejectTransferRequest = {
				messagingId: messagingInviteSession.id,
				agentUserId: userId,
				isCancel: false,
			};

			addBreadcrumb("ui_interaction", "Reject transfer", rejectTransferRequest);

			rejectTransferFn(rejectTransferRequest);
		} else if (!!messagingInviteSession?.id) {
			addBreadcrumb("ui_interaction", "Reject invite", messagingInviteSession);

			rejectMutation.mutate(messagingInviteSession.id);
		}
	}, [messagingInviteSession]);

	const acceptMutation = useMutation({
		mutationFn: (messagingId: string) => acceptFn(messagingId),
		onError: (err, data) => {
			captureError(err, "Error accepting messaging interaction", undefined, {
				messagingId: data,
				type: channelType,
			});
			console.error(err);
		},
	});

	const acceptInteraction = useCallback(() => {
		setDisplayCards(false);

		if (
			messagingInviteSession?.transferState === MessagingTransferState.Offering
		) {
			const acceptTransferRequest: MessagingAcceptTransferRequest = {
				messagingId: messagingInviteSession.id,
			};

			addBreadcrumb("ui_interaction", "Accept transfer", acceptTransferRequest);
			acceptTransferFn(acceptTransferRequest);
		} else if (!!messagingInviteSession?.id) {
			addBreadcrumb("ui_interaction", "Reject invite", messagingInviteSession);
			acceptMutation.mutate(messagingInviteSession.id);
		}
	}, [messagingInviteSession]);

	const description = useMemo(() => {
		if (
			messagingInviteSession?.channelType !== ChannelType.WhatsApp &&
			messagingInviteSession?.channelType !== ChannelType.Webchat
		) {
			return messagingInviteSession?.description;
		} else if (
			messagingInviteSession?.name !==
			messagingInviteSession?.contactInformation
		) {
			return messagingInviteSession?.contactInformation;
		}
		return "";
	}, [messagingInviteSession]);

	const customerName = useMemo(() => {
		if (
			messagingInviteSession?.channelType !== ChannelType.Webchat ||
			messagingInviteSession?.name !== WebchatDefaultCustomerName
		) {
			return messagingInviteSession?.name;
		}
		return intl.formatMessage({
			id: "Interaction.WebchatDefaultCustomerName",
		});
	}, [messagingInviteSession]);

	const interactionName = useMemo(() => {
		if (
			messagingInviteSession?.channelType !== ChannelType.Webchat ||
			messagingInviteSession?.name !== WebchatDefaultCustomerName
		) {
			return messagingInviteSession?.name;
		}
		return intl.formatMessage({
			id: "Interaction.WebchatDefaultInteractionName",
		});
	}, [messagingInviteSession]);

	return (
		displayCards && (
			<>
				<DetailsCard
					contactName={interactionName}
					queueName={messagingInviteSession?.queueName}
					description={description}
					transferInitiatorName={messagingInviteSession?.transferInitiatorName}
					fallbackFrom={
						messagingInviteSession?.fallbackFrom
					}
					offeringVariableText={
						messagingInviteSession?.direction === InteractionDirection.Incoming
							? messagingInviteSession?.offeringVariableText
							: ""
					}
					offeringVariableTextIsAiGenerated={
						messagingInviteSession?.direction === InteractionDirection.Incoming
							? messagingInviteSession?.offeringVariableTextIsAiGenerated
							: undefined
					}
					skills={messagingInviteSession?.skills}
				/>
				<InteractionActionCard
					contactName={customerName}
					icon={icon}
					message={intl.formatMessage({ id: intlMessageId })}
					acceptInteraction={acceptInteraction}
					rejectInteraction={rejectInteraction}
				/>
			</>
		)
	);
};
